/**
 * The workspaces of the shell (I-3641): each is a tab at the bottom with its own
 * arrangement of tiles, and tabs can stand in a group. One store for the top-level
 * shell; `TileWorkspaces.vue` renders it and writes it to `shell.tiles`.
 */
import { computed, nextTick, ref, shallowRef } from "vue";
import {
  connectTileId,
  type ConnectTileCommand,
  type ConnectTileNav,
} from "@blpde/connect";
import {
  canSplitTile,
  findTile,
  insertTile,
  listTiles,
  moveTile,
  patchTile,
  readStoredTree,
  removeTile,
  setSplitRatio,
  type TileAreaSize,
  type TileDrop,
  type TileLeaf,
  type TileNode,
  type TileSide,
} from "./tileTree";

export interface TileWorkspace {
  id: string;
  /** Set by the user; empty: the tab names itself after its tiles. */
  label: string;
  groupId?: string;
  root: TileNode;
  activeTileId: string;
  /** Shown alone; the others stay loaded behind it. */
  maximizedTileId: string | null;
}

export interface TileGroup {
  id: string;
  label: string;
  collapsed: boolean;
}

const workspaces = ref<TileWorkspace[]>([]);
const groups = ref<TileGroup[]>([]);
const activeId = ref<string | null>(null);

const newId = () => crypto.randomUUID();
const newTile = (route: string | null): TileLeaf => ({
  kind: "tile",
  id: newId(),
  route,
});

const active = computed(
  () => workspaces.value.find((w) => w.id === activeId.value) ?? null,
);

/** What each tile last reported as its menu; lives as long as the tile's frame. */
const tileNavs = shallowRef<ReadonlyMap<string, ConnectTileNav>>(new Map());
const activeTileNav = computed(() => {
  const tileId = active.value?.activeTileId;
  const nav = tileId ? tileNavs.value.get(tileId) : undefined;
  return tileId && nav ? { tileId, nav } : null;
});

/** Before something drops every tile from outside (a profile switch): `leave` asks them. */
export interface TileLeaveGuard {
  leave(): Promise<boolean>;
  /** It did not happen after all, or kept these tabs: their tiles go back to where they were. */
  stay(): void;
}
const LEAVE_GUARD_FIELD = "__connectTileLeaveGuard";
type LeaveGuardWindow = Window & {
  [LEAVE_GUARD_FIELD]?: TileLeaveGuard | null;
};
function setLeaveGuard(guard: TileLeaveGuard | null): void {
  (window as LeaveGuardWindow)[LEAVE_GUARD_FIELD] = guard;
}
const allowAll: TileLeaveGuard = { leave: async () => true, stay: () => {} };
/** The page that holds the tiles has it: inside a tile, the page around it. */
function tileLeaveGuard(): TileLeaveGuard {
  try {
    const page = connectTileId() ? window.parent : window;
    return (page as LeaveGuardWindow)[LEAVE_GUARD_FIELD] ?? allowAll;
  } catch {
    return allowAll;
  }
}

/**
 * Tiles whose frame's code runs: only they hear what is sent to them. A frame says
 * this and its menu once, so both last as long as its pane, whatever the tabs do.
 */
const runningTiles = new Set<string>();
function setTileRunning(tileId: string, running: boolean): void {
  if (running) {
    runningTiles.add(tileId);
    return;
  }
  runningTiles.delete(tileId);
  if (tileNavs.value.has(tileId)) forgetTileNavs([tileId]);
}

function setTileNav(tileId: string, nav: ConnectTileNav): void {
  tileNavs.value = new Map(tileNavs.value).set(tileId, nav);
}

function sendToTile(tileId: string, command: ConnectTileCommand): void {
  const pane = [
    ...document.querySelectorAll<HTMLElement>("[data-tile-id]"),
  ].find((el) => el.dataset.tileId === tileId);
  const frame = pane?.querySelector("iframe");
  frame?.contentWindow?.postMessage(command, location.origin);
}

function update(
  id: string,
  change: (workspace: TileWorkspace) => TileWorkspace,
): void {
  workspaces.value = workspaces.value.map((w) => (w.id === id ? change(w) : w));
}

/** Tabs of a group stand together, at the place of its first; an empty group is gone. */
function arrange(list: readonly TileWorkspace[]): void {
  const known = new Set(groups.value.map((group) => group.id));
  const ordered: TileWorkspace[] = [];
  for (const workspace of list) {
    const groupId = workspace.groupId;
    if (ordered.includes(workspace)) continue;
    if (groupId && known.has(groupId))
      ordered.push(...list.filter((w) => w.groupId === groupId));
    else ordered.push(workspace);
  }
  workspaces.value = ordered;
  const used = new Set(ordered.map((w) => w.groupId));
  if (groups.value.some((group) => !used.has(group.id)))
    groups.value = groups.value.filter((group) => used.has(group.id));
}

function createGroup(workspaceId: string, label: string): void {
  const id = newId();
  groups.value = [...groups.value, { id, label, collapsed: false }];
  addToGroup(workspaceId, id);
}

function addToGroup(workspaceId: string, groupId: string): void {
  const moving = workspaces.value.find((w) => w.id === workspaceId);
  if (!moving) return;
  const next = { ...moving, groupId };
  const rest = workspaces.value.filter((w) => w.id !== workspaceId);
  const lastOfGroup = rest.map((w) => w.groupId).lastIndexOf(groupId);
  if (lastOfGroup < 0) {
    const at = workspaces.value.indexOf(moving);
    rest.splice(at, 0, next);
  } else rest.splice(lastOfGroup + 1, 0, next);
  arrange(rest);
}

/** Out of its group: the tab stands right behind it. */
function removeFromGroup(workspaceId: string): void {
  const moving = workspaces.value.find((w) => w.id === workspaceId);
  if (!moving?.groupId) return;
  const groupId = moving.groupId;
  const rest = workspaces.value.filter((w) => w.id !== workspaceId);
  const lastOfGroup = rest.map((w) => w.groupId).lastIndexOf(groupId);
  const { groupId: _left, ...next } = moving;
  rest.splice(
    lastOfGroup < 0 ? workspaces.value.indexOf(moving) : lastOfGroup + 1,
    0,
    next,
  );
  arrange(rest);
}

/**
 * The tabs leave this window for another one, open and as they are: nothing is
 * closed. Returns the groups they take along.
 */
function handOver(ids: readonly string[]): TileGroup[] {
  const leaving = new Set(ids);
  const taken = groups.value.filter((group) =>
    workspaces.value.some((w) => leaving.has(w.id) && w.groupId === group.id),
  );
  forgetTileNavs(
    workspaces.value
      .filter((w) => leaving.has(w.id))
      .flatMap((w) => listTiles(w.root).map((tile) => tile.id)),
  );
  const index = workspaces.value.findIndex((w) => w.id === activeId.value);
  arrange(workspaces.value.filter((w) => !leaving.has(w.id)));
  if (activeId.value && leaving.has(activeId.value))
    activeId.value =
      workspaces.value[
        Math.max(0, Math.min(index, workspaces.value.length - 1))
      ]?.id ?? null;
  return taken;
}

function renameGroup(id: string, label: string): void {
  groups.value = groups.value.map((g) => (g.id === id ? { ...g, label } : g));
}

function toggleGroup(id: string): void {
  groups.value = groups.value.map((g) =>
    g.id === id ? { ...g, collapsed: !g.collapsed } : g,
  );
}

/** The tabs stay, as single tabs. */
function dissolveGroup(id: string): void {
  groups.value = groups.value.filter((group) => group.id !== id);
  workspaces.value = workspaces.value.map((w) => {
    if (w.groupId !== id) return w;
    const { groupId: _left, ...rest } = w;
    return rest;
  });
}

function addWorkspace(label = "", route: string | null = null): TileWorkspace {
  const tile = newTile(route);
  const workspace: TileWorkspace = {
    id: newId(),
    label,
    root: tile,
    activeTileId: tile.id,
    maximizedTileId: null,
  };
  workspaces.value = [...workspaces.value, workspace];
  activeId.value = workspace.id;
  return workspace;
}

function forgetTileNavs(tileIds: readonly string[]): void {
  const next = new Map(tileNavs.value);
  tileIds.forEach((tileId) => next.delete(tileId));
  tileNavs.value = next;
}

function closeWorkspace(id: string): void {
  const closing = workspaces.value.find((w) => w.id === id);
  if (closing) forgetTileNavs(listTiles(closing.root).map((tile) => tile.id));
  const index = workspaces.value.findIndex((w) => w.id === id);
  arrange(workspaces.value.filter((w) => w.id !== id));
  if (activeId.value === id) {
    activeId.value = workspaces.value[Math.max(0, index - 1)]?.id ?? null;
  }
}

function renameWorkspace(id: string, label: string): void {
  update(id, (w) => ({ ...w, label }));
}

/**
 * `targetId` null: to the end, as the tab strip reports it. Like in a browser, the
 * tab joins the group of the tab it lands on, and leaves its own otherwise.
 */
function moveWorkspace(id: string, targetId: string | null): void {
  const moving = workspaces.value.find((w) => w.id === id);
  if (!moving || id === targetId) return;
  const rest = workspaces.value.filter((w) => w.id !== id);
  const at = targetId ? rest.findIndex((w) => w.id === targetId) : rest.length;
  const groupId = at < 0 ? undefined : rest[at]?.groupId;
  const { groupId: _left, ...plain } = moving;
  rest.splice(
    at < 0 ? rest.length : at,
    0,
    groupId ? { ...plain, groupId } : plain,
  );
  arrange(rest);
}

/** The size of the area the tiles lie in; hidden, the last one stays. */
const areaSize = shallowRef<TileAreaSize | null>(null);
function setAreaSize(size: TileAreaSize): void {
  if (size.width > 0 && size.height > 0) areaSize.value = size;
}

function canSplit(
  workspaceId: string,
  tileId: string,
  side: TileSide,
): boolean {
  const workspace = workspaces.value.find((w) => w.id === workspaceId);
  return (
    !!workspace && canSplitTile(workspace.root, tileId, side, areaSize.value)
  );
}

/** No room: a route still opens, in a tab of its own; an empty tile is just not made. */
function splitTile(
  workspaceId: string,
  tileId: string,
  side: TileSide,
  route: string | null = null,
): string {
  if (!canSplit(workspaceId, tileId, side))
    return route === null ? tileId : addWorkspace("", route).activeTileId;
  const tile = newTile(route);
  update(workspaceId, (w) => ({
    ...w,
    root: insertTile(w.root, tileId, side, tile, newId()),
    activeTileId: tile.id,
    maximizedTileId: null,
  }));
  return tile.id;
}

/** The last tile leaves an empty one behind, so a workspace always offers the apps. */
function closeTile(workspaceId: string, tileId: string): void {
  forgetTileNavs([tileId]);
  update(workspaceId, (w) => {
    const root = removeTile(w.root, tileId) ?? newTile(null);
    const stillActive = findTile(root, w.activeTileId);
    return {
      ...w,
      root,
      activeTileId: stillActive ? w.activeTileId : listTiles(root)[0].id,
      maximizedTileId: w.maximizedTileId === tileId ? null : w.maximizedTileId,
    };
  });
}

function setTileRoute(
  workspaceId: string,
  tileId: string,
  route: string | null,
): void {
  update(workspaceId, (w) => {
    // Its name belongs to the page it leaves; the frame names the new one (I-3777).
    const moved = findTile(w.root, tileId)?.route !== route;
    return {
      ...w,
      root: patchTile(
        w.root,
        tileId,
        moved ? { route, title: undefined, area: undefined } : { route },
      ),
    };
  });
}

function setActiveTile(workspaceId: string, tileId: string): void {
  const workspace = workspaces.value.find((w) => w.id === workspaceId);
  if (workspace?.activeTileId === tileId) return;
  // The active tile is in view: the logo and the shared navbar act on it.
  update(workspaceId, (w) => ({
    ...w,
    activeTileId: tileId,
    maximizedTileId: w.maximizedTileId === tileId ? tileId : null,
  }));
}

/** The user is in this tile, wherever it lies. Unknown tiles were closed meanwhile. */
function pressTile(tileId: string): void {
  const workspace = workspaces.value.find((w) => findTile(w.root, tileId));
  if (workspace) setActiveTile(workspace.id, tileId);
}

function toggleMaximized(workspaceId: string, tileId: string): void {
  // The tile shown alone is the active one: the shared navbar acts on it.
  update(workspaceId, (w) => ({
    ...w,
    activeTileId: tileId,
    maximizedTileId: w.maximizedTileId === tileId ? null : tileId,
  }));
}

function dropTile(
  workspaceId: string,
  tileId: string,
  targetId: string,
  drop: TileDrop,
): void {
  update(workspaceId, (w) => ({
    ...w,
    root: moveTile(w.root, tileId, targetId, drop, newId()),
  }));
}

function resizeSplit(
  workspaceId: string,
  splitId: string,
  ratio: number,
): void {
  update(workspaceId, (w) => ({
    ...w,
    root: setSplitRatio(w.root, splitId, ratio),
  }));
}

/** A tile reported its route or caption. Unknown tiles were closed meanwhile. */
function reportTile(
  tileId: string,
  state: { path?: string; title?: string; area?: string },
): void {
  const workspace = workspaces.value.find((w) => findTile(w.root, tileId));
  if (!workspace) return;
  const tile = findTile(workspace.root, tileId);
  const route = state.path ?? tile?.route ?? null;
  const title = state.title ?? tile?.title;
  const area = state.area ?? tile?.area;
  // Nothing to say and no field are the same: it is not worth a write.
  const same = (a?: string, b?: string) => (a || "") === (b || "");
  if (
    tile?.route === route &&
    same(tile?.title, title) &&
    same(tile?.area, area)
  )
    return;
  update(workspace.id, (w) => ({
    ...w,
    root: patchTile(w.root, tileId, { route, title, area }),
  }));
}

/** Whether the stored tabs of this window are in: until then a route waits as a tab. */
const hostReady = ref(false);
function setHostReady(ready: boolean): void {
  hostReady.value = ready;
}

/**
 * A page opened from anywhere lives in a tab. Into the active tile; before the
 * stored tabs are in, a route of its own becomes a tab of its own, and the start
 * page needs none: a window without tabs starts on it anyway.
 */
function openRoute(route: string): void {
  const workspace = active.value;
  if (hostReady.value && workspace) {
    openInTile(workspace.activeTileId, route);
    return;
  }
  if (route !== "/") addWorkspace("", route);
}

/** „Daneben öffnen“: the route takes an empty active tile, else a new one to its right. */
function openBeside(route: string | null): void {
  const workspace = active.value;
  if (!workspace) {
    addWorkspace("", route);
    return;
  }
  const current = findTile(workspace.root, workspace.activeTileId);
  if (current && current.route === null)
    setTileRoute(workspace.id, current.id, route);
  else if (canSplit(workspace.id, workspace.activeTileId, "right"))
    splitTile(workspace.id, workspace.activeTileId, "right", route);
  else addWorkspace("", route);
}

/**
 * `shell.tiles`: a merge patch per changed workspace and per window, so another
 * browser window or device that changes its own does not overwrite this one. A
 * window (`tileWindows.ts`) lists its tabs in order, the active one and its
 * groups. Each entry is a one-element array: a merge patch replaces an array
 * whole, where it would leave the old members of a replaced object behind. An
 * array holds no null (master refuses it, `invalid_config`). A tile's title
 * and area are stored as well (I-3806): a tab not started yet after a reload,
 * and a closed window, still say where they stood. The running tile reports them
 * anew.
 */
export interface StoredWindow {
  order: string[];
  activeId?: string;
  groups?: TileGroup[];
  /** Moved here on purpose: comes up even without restoring (`shell.tiles.restore`). */
  handover?: boolean;
  /** Set with every write of the window: closed windows are listed by it. */
  savedAt?: string;
}
export interface StoredTiles {
  workspaces?: Record<string, [StoredWorkspace]>;
  windows?: Record<string, [StoredWindow]>;
}
type StoredWorkspace = TileWorkspace;

function stripped(value: unknown): unknown {
  return JSON.parse(
    JSON.stringify(value, (key, field) =>
      field === null ? undefined : field,
    ),
  );
}

export function storedTiles(
  windowId: string,
  list: readonly TileWorkspace[],
  id: string | null,
  tabGroups: readonly TileGroup[] = [],
): StoredTiles {
  const window: StoredWindow = {
    order: list.map((w) => w.id),
    groups: [...tabGroups],
  };
  if (id) window.activeId = id;
  const stored: StoredTiles = {
    workspaces: {},
    windows: { [windowId]: [window] },
  };
  for (const workspace of list)
    stored.workspaces![workspace.id] = [stripped(workspace) as StoredWorkspace];
  return stored;
}

/** The merge patch from one stored state to the next; null when nothing changed. */
export function tilesPatch(
  before: StoredTiles,
  after: StoredTiles,
): Record<string, unknown> | null {
  const patch: Record<string, unknown> = {};
  const changed: Record<string, unknown> = {};
  const was = before.workspaces ?? {};
  const now = after.workspaces ?? {};
  for (const id of new Set([...Object.keys(was), ...Object.keys(now)])) {
    if (JSON.stringify(was[id]) !== JSON.stringify(now[id]))
      changed[id] = now[id] ?? null;
  }
  if (Object.keys(changed).length) patch.workspaces = changed;
  const windows: Record<string, unknown> = {};
  const wasWindows = before.windows ?? {};
  const nowWindows = after.windows ?? {};
  for (const id of new Set([
    ...Object.keys(wasWindows),
    ...Object.keys(nowWindows),
  ])) {
    if (JSON.stringify(wasWindows[id]) !== JSON.stringify(nowWindows[id]))
      windows[id] = nowWindows[id] ?? null;
  }
  if (Object.keys(windows).length) patch.windows = windows;
  return Object.keys(patch).length ? patch : null;
}

/** How to give a tile that gave up loading a fresh frame; null for any other tile. */
type GaveUpRestart = (tileId: string) => (() => void) | null;
let gaveUpRestart: GaveUpRestart | null = null;
function setGaveUpRestart(restart: GaveUpRestart | null): void {
  gaveUpRestart = restart;
}

/**
 * Into a tile that is already there: an empty one starts its frame with the route,
 * a running one navigates, so its app's own leave guards still ask.
 */
function openInTile(tileId: string, route: string): void {
  const workspace = workspaces.value.find((w) => findTile(w.root, tileId));
  const tile = workspace && findTile(workspace.root, tileId);
  if (!workspace || !tile) return;
  // A frame still loading hears nothing: it starts there, or goes there once it runs.
  // One that gave up never starts by itself: it gets a fresh frame there.
  const restart = gaveUpRestart?.(tileId) ?? null;
  if (tile.route === null || restart || !runningTiles.has(tileId))
    setTileRoute(workspace.id, tileId, route);
  else sendToTile(tileId, { type: "connect.tile.navigate", path: route });
  activeId.value = workspace.id;
  setActiveTile(workspace.id, tileId);
  // The route reaches the pane with the flush the change above queued: only a tick
  // asked for after it comes later, and the fresh frame starts there.
  if (restart) void nextTick(restart);
}

/** The tile a list last opened into, per list's tile, so repeated opens stay there. */
const lastTarget = new Map<string, string>();

/** „Daneben“ from a tile: into the tile it used last or the next one; a new one if none, or if wanted. */
function openFromTile(
  sourceTileId: string,
  route: string,
  reuse: boolean,
): void {
  const workspace = workspaces.value.find((w) =>
    findTile(w.root, sourceTileId),
  );
  if (!workspace) return;
  const others = listTiles(workspace.root).filter(
    (tile) => tile.id !== sourceTileId,
  );
  if (!reuse || !others.length) {
    lastTarget.set(
      sourceTileId,
      splitTile(workspace.id, sourceTileId, "right", route),
    );
    return;
  }
  const remembered = others.find(
    (tile) => tile.id === lastTarget.get(sourceTileId),
  );
  const target = remembered ?? others[0];
  lastTarget.set(sourceTileId, target.id);
  openInTile(target.id, route);
}

/** From a plain page: that page and the record side by side, in a new workspace. */
function openPageAndBeside(page: string | null, route: string): void {
  const workspace = addWorkspace("", page);
  if (page) splitTile(workspace.id, workspace.activeTileId, "right", route);
  else setTileRoute(workspace.id, workspace.activeTileId, route);
}

/** The single bar's keys from before windows, if `shell.tiles` still has them. */
export function legacyWindow(stored: unknown): StoredWindow | null {
  const value = stored as Record<string, unknown> | null | undefined;
  if (!value || value.windows || !Array.isArray(value.order)) return null;
  return {
    order: value.order as string[],
    activeId: value.activeId as string | undefined,
    groups: value.groups as TileGroup[] | undefined,
  };
}

/** The tabs of one browser window; another window's stay where they are. */
function hydrate(stored: unknown, windowId: string): void {
  const value = stored as StoredTiles | null | undefined;
  const entries = value?.workspaces;
  if (!entries || typeof entries !== "object" || Array.isArray(entries)) return;
  // Before windows there was one bar for all: the first window takes its tabs.
  const window = value.windows
    ? value.windows[windowId]?.[0]
    : (legacyWindow(value) ?? undefined);
  const ids = Array.isArray(window?.order)
    ? window.order.filter((id) => entries[id])
    : [];
  const read: TileWorkspace[] = [];
  for (const id of ids) {
    const raw: Partial<TileWorkspace> | undefined = Array.isArray(entries[id])
      ? entries[id][0]
      : undefined;
    const root = readStoredTree(raw?.root);
    if (!root || !raw) continue;
    const tiles = listTiles(root);
    const maximizedTileId = tiles.some((t) => t.id === raw.maximizedTileId)
      ? raw.maximizedTileId!
      : null;
    read.push({
      id,
      label: typeof raw.label === "string" ? raw.label : "",
      ...(typeof raw.groupId === "string" ? { groupId: raw.groupId } : {}),
      root,
      activeTileId:
        maximizedTileId ??
        (tiles.some((t) => t.id === raw.activeTileId)
          ? raw.activeTileId!
          : tiles[0].id),
      maximizedTileId,
    });
  }
  groups.value = Array.isArray(window?.groups)
    ? window.groups.filter((group) => group && typeof group.id === "string")
    : [];
  arrange(read);
  activeId.value = read.some((w) => w.id === window?.activeId)
    ? (window!.activeId as string)
    : (read[0]?.id ?? null);
}

function reset(): void {
  lastTarget.clear();
  workspaces.value = [];
  groups.value = [];
  activeId.value = null;
}

export function useTileWorkspaces() {
  return {
    workspaces,
    groups,
    activeId,
    active,
    createGroup,
    addToGroup,
    removeFromGroup,
    renameGroup,
    toggleGroup,
    dissolveGroup,
    handOver,
    setLeaveGuard,
    hostReady,
    setHostReady,
    setTileRunning,
    openRoute,
    tileLeaveGuard,
    addWorkspace,
    closeWorkspace,
    renameWorkspace,
    moveWorkspace,
    setAreaSize,
    canSplit,
    splitTile,
    closeTile,
    setTileRoute,
    setActiveTile,
    pressTile,
    toggleMaximized,
    dropTile,
    resizeSplit,
    reportTile,
    activeTileNav,
    setTileNav,
    sendToTile,
    openBeside,
    openFromTile,
    setGaveUpRestart,
    openInTile,
    openPageAndBeside,
    hydrate,
    reset,
  };
}
